import axios from 'axios'

const RAW_BASE_URL = process.env.REACT_APP_API_BASE_URL || ''
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1'

// API 베이스 URL 결정 로직
// 우선순위: localStorage override > 환경변수 > 호스트 기반 추론
const getBaseUrl = () => {
    const host = (typeof window !== 'undefined') ? window.location.hostname : ''
    const isLocalhost = /^(localhost|127\.0\.0\.1)$/i.test(host)
    const override = (typeof window !== 'undefined') ? localStorage.getItem('api.baseUrl.override') : null

    // env/override에 "."를 넣는 경우(동일 오리진 의도) axios의 상대경로 해석 때문에
    // 현재 라우트 경로를 기준으로 붙어버린다.
    // 예) /requests/new 에서 baseURL="./v1" → /requests/v1/... 로 호출됨(nginx 프록시 미적용)
    // 따라서 "."/ "./"는 "루트"로 정규화한다.
    const normalize = (v) => {
        if (!v) return v
        const s = String(v).trim()
        if (s === '.' || s === './') return ''
        return s
    }

    if (override && override.length > 0) return normalize(override)

    if (RAW_BASE_URL) {
        // 로컬에서 프로덕션 베이스가 주입된 빌드를 실행 중이면 localhost로 강제 전환
        if (isLocalhost && /api\.sgez\./.test(RAW_BASE_URL)) return 'http://localhost:8080'
        return normalize(RAW_BASE_URL)
    }

    // 기본값은 "동일 호스트" 호출(상대경로)로 둔다.
    // - BE/FE를 같은 서버에서 Nginx로 프록시할 때 가장 단순/안정적 (CORS/도메인 의존 제거)
    // - 로컬 개발(webpack dev server 등)에서만 8080으로 직접 호출
    return isLocalhost ? 'http://localhost:8080' : ''
}

const BASE_URL = getBaseUrl()
// 서버는 /v1 을 전역 context-path로 사용. 프론트는 환경변수 API_VERSION으로 제어
const BASE_WITH_VERSION = `${BASE_URL.replace(/\/$/, '')}/${API_VERSION}`

export const api = axios.create({
    baseURL: BASE_WITH_VERSION,
    withCredentials: false, // JWT 토큰 기반으로 변경
})

// 최근 서버 traceId(응답 헤더) 캐시: FE 오류 리포팅 시 함께 전송
let lastTraceId = null
export function getLastTraceId() {
    return lastTraceId
}

function sanitizeClientLogString(s, max = 2000) {
    if (!s) return s
    let v = String(s)
    // 토큰/헤더 등 마스킹(방어적)
    v = v.replace(/bearer\s+[-a-z0-9._~+/]+=*/ig, 'Bearer ***')
    v = v.replace(/(access_token|refresh_token|authorization|cookie)\s*[:=]\s*([^\s"']+)/ig, '$1=***')
    v = v.replace(/\r?\n/g, ' ')
    if (v.length > max) v = v.slice(0, max) + '…'
    return v
}

// FE 오류/이벤트를 BE로 전송(중앙로그가 없을 때 서버 로그로 모으기)
export async function reportClientLog(payload) {
    try {
        const url = `${BASE_WITH_VERSION.replace(/\/$/, '')}/logs/client`
        const safe = {
            level: payload?.level || 'error',
            source: sanitizeClientLogString(payload?.source, 80),
            route: sanitizeClientLogString(payload?.route, 200),
            url: sanitizeClientLogString(payload?.url, 500),
            message: sanitizeClientLogString(payload?.message, 2000),
            stack: sanitizeClientLogString(payload?.stack, 8000),
            userAgent: sanitizeClientLogString(payload?.userAgent, 300),
        }
        // 인터셉터/토큰 갱신 루프를 피하기 위해 raw axios 사용
        await axios.post(url, safe, {
            headers: {
                'X-Client-Request-Id': safeRandomUUID(),
            },
        })
    } catch (_) {
        // ignore
    }
}

// crypto.randomUUID()는 일부 브라우저/환경(구형 Edge, 일부 WebView 등)에서 미지원일 수 있음.
// Idempotency-Key 용도로는 "충분히 유니크"하면 되므로 v4 폴백을 제공한다.
function safeRandomUUID() {
    const c = (typeof crypto !== 'undefined' && crypto) ? crypto : ((typeof window !== 'undefined' && window.crypto) ? window.crypto : null)
    if (c && typeof c.randomUUID === 'function') return c.randomUUID()

    if (c && typeof c.getRandomValues === 'function') {
        const bytes = new Uint8Array(16)
        c.getRandomValues(bytes)
        // RFC4122 v4
        bytes[6] = (bytes[6] & 0x0f) | 0x40
        bytes[8] = (bytes[8] & 0x3f) | 0x80
        const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
    }

    // 최후의 폴백(crypto 미존재): 멱등성 키로만 사용되므로 충돌 확률 낮추기
    return `idemp_${Date.now()}_${Math.random().toString(16).slice(2)}_${Math.random().toString(16).slice(2)}`
}

// 전역 메타데이터 캐시: roles
let metaCache = { roles: null }

export async function fetchRolesMeta() {
    if (metaCache.roles) return metaCache.roles
    const res = await api.get('/meta/roles')
    const data = (res?.data && res.data.data) ? res.data.data : res.data
    metaCache.roles = Array.isArray(data) ? data : []
    return metaCache.roles
}

export async function cacheRolesMeta() {
    try {
        const roles = await fetchRolesMeta()
        if (typeof window !== 'undefined') {
            localStorage.setItem('meta.roles', JSON.stringify(roles))
        }
    } catch (_) {
        // ignore failures; UI will fall back to static labels
    }
}

export function decodeJwtPayload(token) {
    try {
        if (!token) return null
        const parts = String(token).split('.')
        if (parts.length < 2) return null
        const b64url = parts[1]
        const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/')
        const padded = b64 + '==='.slice((b64.length + 3) % 4)
        const json = atob(padded)
        return JSON.parse(json)
    } catch {
        return null
    }
}

export function getJwtExpMs(token) {
    const payload = decodeJwtPayload(token)
    const expSec = payload?.exp
    if (!expSec || typeof expSec !== 'number') return null
    return expSec * 1000
}

export function isAccessTokenExpired(token, skewMs = 5000) {
    const expMs = getJwtExpMs(token)
    if (!expMs) return false
    return Date.now() >= (expMs - skewMs)
}

// SSE 연결 유틸 (알림 스트림)
// 알림 수신을 위한 SSE 연결 (헤더 미지원 한계로 쿼리로 토큰 전달)
export function connectSse(getToken, onMessage) {
    const base = `${BASE_WITH_VERSION.replace(/\/$/, '')}/notifications/stream`

    let closed = false
    let es = null
    let retryMs = 1000
    const maxRetryMs = 30000
    let timer = null

    const cleanup = () => {
        if (timer) {
            clearTimeout(timer)
            timer = null
        }
        if (es) {
            try { es.close() } catch { }
            es = null
        }
    }

    const connect = async () => {
        if (closed) return
        let token = (typeof getToken === 'function') ? getToken() : null
        if (!token) return

        // 만료/임박 토큰이면 먼저 refresh 후 연결(업무시간 탭 유지 + 로그 오염 방지)
        if (isAccessTokenExpired(token, 30000)) {
            const refreshed = await refreshTokensIfPossible()
            if (!refreshed?.accessToken) {
                closed = true
                cleanup()
                return
            }
            token = refreshed.accessToken
        }
        const qs = new URLSearchParams({ access_token: token })
        es = new EventSource(`${base}?${qs.toString()}`)

        es.addEventListener('notify', (e) => {
            try { onMessage && onMessage(JSON.parse(e.data)) } catch { }
        })
        es.addEventListener('ping', () => { /* keep-alive */ })
        es.addEventListener('init', () => { retryMs = 1000 })

        es.onerror = () => {
            // 토큰 만료/네트워크 오류 등 원인 구분이 어려워, 연결을 닫고 최신 토큰으로 재연결한다.
            cleanup()
            if (closed) return
            const cur = (typeof getToken === 'function') ? getToken() : null
            if (cur && isAccessTokenExpired(cur, 0)) {
                // 만료면 refresh 후 즉시 재연결 (실패하면 내부에서 로그아웃 처리)
                void (async () => {
                    const refreshed = await refreshTokensIfPossible()
                    if (!refreshed?.accessToken) {
                        closed = true
                        cleanup()
                        return
                    }
                    retryMs = 1000
                    void connect()
                })()
                return
            }
            const next = Math.min(maxRetryMs, retryMs)
            retryMs = Math.min(maxRetryMs, retryMs * 2)
            timer = setTimeout(() => { void connect() }, next)
        }
    }

    void connect()

    return {
        close: () => {
            closed = true
            cleanup()
        },
    }
}

// /auth 경로 판별
// /auth/* 경로 여부 판별 (로그인/리프레시/로그아웃)
const isAuthPath = (url) => {
    if (!url) return false
    // 절대/상대 경로 모두 대응
    return /(^|\/)auth\/(login|refresh|logout)(\/|$)/.test(url)
}

// 요청 인터셉터: Authorization + Idempotency-Key 주입
api.interceptors.request.use((config) => {
    config.headers = config.headers || {}

    // 모든 요청에 클라이언트 요청 ID 부여(서버 traceId와 별도로, FE 측 상관관계 용)
    if (!config.headers['X-Client-Request-Id']) {
        config.headers['X-Client-Request-Id'] = safeRandomUUID()
    }

    // JWT 토큰 헤더 주입
    // - /auth/* 요청에는 Authorization 헤더를 붙이지 않음
    if (!isAuthPath(config.url)) {
        const token = localStorage.getItem('accessToken')
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`
        }
    }

    // 멱등성 보장을 위한 키(POST/PUT/PATCH/DELETE)에 자동 주입
    if (config.method !== 'get' && !config.headers['Idempotency-Key']) {
        config.headers['Idempotency-Key'] = safeRandomUUID()
    }

    return config
})

let refreshingPromise = null
let logoutInProgress = false

function clearAuthStorage() {
    try { localStorage.removeItem('accessToken') } catch { }
    try { localStorage.removeItem('refreshToken') } catch { }
    try { localStorage.removeItem('auth.user') } catch { }
}

function triggerLogoutRedirect() {
    if (logoutInProgress) return
    logoutInProgress = true
    try { window.dispatchEvent(new CustomEvent('auth:logout')) } catch { }
    clearAuthStorage()
    if (typeof window !== 'undefined') {
        const path = window.location?.pathname || ''
        if (path !== '/login') window.location.href = '/login'
    }
}

export async function refreshTokensIfPossible() {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken || refreshToken === 'undefined' || refreshToken === 'null') {
        triggerLogoutRedirect()
        return null
    }
    try {
        if (!refreshingPromise) {
            // 인터셉터 루프를 피하기 위해 raw axios 사용
            refreshingPromise = axios.post(`${BASE_WITH_VERSION}/auth/refresh`, { refreshToken }, {
                headers: {
                    'X-Client-Request-Id': safeRandomUUID(),
                },
            })
        }
        const refreshResponse = await refreshingPromise
        refreshingPromise = null

        const { accessToken, refreshToken: newRefreshToken } = (refreshResponse?.data && refreshResponse.data.data)
            ? refreshResponse.data.data
            : refreshResponse.data
        if (!accessToken || !newRefreshToken) throw new Error('invalid_refresh_response')

        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('refreshToken', newRefreshToken)
        try { window.dispatchEvent(new CustomEvent('auth:tokenRefreshed', { detail: { accessToken } })) } catch { }
        return { accessToken, refreshToken: newRefreshToken }
    } catch (_) {
        refreshingPromise = null
        triggerLogoutRedirect()
        return null
    }
}

// 응답 인터셉터: 401 시 리프레시 후 원요청 재시도
api.interceptors.response.use(
    (res) => {
        // 서버가 내려주는 traceId를 캐시해 두었다가, FE 오류 리포팅 등에 활용
        const tid = res?.headers ? (res.headers['x-trace-id'] || res.headers['x-correlation-id']) : null
        if (tid) lastTraceId = tid
        return res
    },
    async (error) => {
        const original = error?.config || {}
        // 에러에도 traceId를 부착(디버깅 편의)
        try {
            const tid = error?.response?.headers ? (error.response.headers['x-trace-id'] || error.response.headers['x-correlation-id']) : null
            if (tid) {
                lastTraceId = tid
                error.__traceId = tid
            }
        } catch (_) { }

        // 401 에러 시 토큰 갱신 시도 (로그인 요청 제외), 무한루프 방지 가드
        if (error?.response?.status === 401 && !isAuthPath(original.url) && !original.__retried401) {
            // 비로그인 사용자(토큰/유저정보 없음)가 "공개 페이지"를 보고 있을 때,
            // 일부 백그라운드 요청(/meta/roles 등)이 401을 내면 무조건 /login 으로 튕기는 문제가 생길 수 있다.
            // 따라서 "인증 상태로 보이는 경우"에만 로그아웃 리다이렉트를 수행한다.
            const accessToken = localStorage.getItem('accessToken')
            const refreshToken = localStorage.getItem('refreshToken')
            const userData = localStorage.getItem('auth.user')
            const hasAnyAuthState = Boolean(
                (accessToken && accessToken !== 'undefined' && accessToken !== 'null')
                || (refreshToken && refreshToken !== 'undefined' && refreshToken !== 'null')
                || (userData && userData !== 'undefined' && userData !== 'null')
            )

            if (!hasAnyAuthState) {
                return Promise.reject(error)
            }

            const refreshed = await refreshTokensIfPossible()
            if (refreshed?.accessToken) {
                // 원래 요청 재시도 (Authorization 갱신)
                original.headers = original.headers || {}
                original.headers['Authorization'] = `Bearer ${refreshed.accessToken}`
                original.__retried401 = true
                return api.request(original)
            }
        }
        return Promise.reject(error)
    }
)
